import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layout, Typography, Input, Button, Tooltip, Avatar, Modal, Empty,
  Spin, Alert, Form, Popconfirm, Checkbox, message, Tag
} from 'antd';
import {
  UserOutlined, LogoutOutlined, LockOutlined, UnlockOutlined,
  SaveOutlined, PlusOutlined, DeleteOutlined, SearchOutlined,
  CloseOutlined
} from '@ant-design/icons';
import EnvVersionLabel from './Envversionlabel';

const { Header, Sider, Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

// ── Config ──────────────────────────────────────────────────────────────
// Public folder is served at the web root, so public/rules/section_definitions.json
// is reachable at /rules/section_definitions.json.
const SECTION_DEFINITIONS_URL = '/rules/section_definitions.json';

const EDITABLE_ROLES = ['hcc_reviewer'];


// Section types whose "codes don't need support" checkbox defaults to CHECKED.
// All other keys default to unchecked. If the JSON file itself carries a
// `no_code_support_needed` field on an entry, that value wins over this default.
const DEFAULT_NO_SUPPORT_KEYS = new Set([
  'assessment',
  'plan',
  'subjective',
  'physical_exam',
]);

/**
 * SectionDefinitionsEditor
 *
 * View/edit page for the `section_types` block of section_definitions.json.
 *
 * Shell mirrors WorkQueuePage.jsx: 60px collapsed Sider, 64px dark header with
 * a breadcrumb on the left and action buttons on the right, gradient Content
 * background.
 *
 * Content body is a two-column card:
 *   - Left panel: searchable list of section_type keys, with Add-new (modal)
 *     at the top and per-row delete (in edit mode only).
 *   - Right panel: selected entry's description and header_variants.
 *
 * Mode:
 *   - Defaults to read-only (locked). Lock button in header toggles edit mode.
 *   - Save button sits next to the lock button — UI only, NOT wired to any
 *     persistence yet. Clicking it shows an info message.
 *
 * Data:
 *   - Fetched from /rules/section_definitions.json (public folder). If the
 *     file is missing or invalid, a small "config file cannot be found" alert
 *     is shown in place of the editor.
 *   - All edits are in-memory only. No backend. Reloading the page resets.
 *
 * @param {Object} props
 * @param {string} props.username
 * @param {string} props.sessionId
 * @param {string} props.userRole
 * @param {Array}  props.userFeatures
 * @param {Array}  props.tabOrder
 * @param {function} props.onLogout
 */
const SectionDefinitionsEditor = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();

  // ── State ────────────────────────────────────────────────────────────
  const [loading, setLoading]         = useState(true);
  const [loadError, setLoadError]     = useState(null);
  const [sectionTypes, setSectionTypes] = useState({}); // { key: { description, header_variants } }
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchText, setSearchText]   = useState('');
  const [editMode, setEditMode]       = useState(false); // false = locked / read-only

  // Add-new modal state
  const [addModalOpen, setAddModalOpen]   = useState(false);
  const [newKey, setNewKey]               = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newVariantsText, setNewVariantsText] = useState(''); // one variant per line
  const [newKeyError, setNewKeyError]     = useState(null);
  const [newNoCodeSupport, setNewNoCodeSupport] = useState(false);

  const canEdit = EDITABLE_ROLES.includes(userRole);


  // ── Fetch section_definitions.json ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    fetch(SECTION_DEFINITIONS_URL)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        if (cancelled) return;
        const types = (data && data.section_types) || {};

        // Augment each entry with `no_code_support_needed`. Honor the value
        // if the JSON already carries it; otherwise default from the rule:
        // true for the four known clinical-content keys, false for the rest.
        const augmented = {};
        Object.keys(types).forEach(key => {
          const entry = types[key] || {};
          augmented[key] = {
            ...entry,
            no_code_support_needed:
              entry.no_code_support_needed ?? DEFAULT_NO_SUPPORT_KEYS.has(key),
          };
        });

        setSectionTypes(augmented);
        const keys = Object.keys(augmented);
        if (keys.length > 0) {
          setSelectedKey(keys[0]); // default to first key, e.g. "assessment"
        }
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load section_definitions.json:', error);
        setLoadError(error.message || 'Unknown error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // ── Derived: keys list and filtered keys ─────────────────────────────
  const allKeys = useMemo(() => Object.keys(sectionTypes), [sectionTypes]);

  const filteredKeys = useMemo(() => {
    if (!searchText.trim()) return allKeys;
    const q = searchText.toLowerCase();
    return allKeys.filter(k => k.toLowerCase().includes(q));
  }, [allKeys, searchText]);

  const selectedEntry = selectedKey ? sectionTypes[selectedKey] : null;

  // ── Handlers: lock / save ────────────────────────────────────────────
  const handleLockToggle = () => setEditMode(prev => !prev);

  const handleSave = () => {
    // Placeholder. No persistence wired up yet — kept here so the UI is
    // complete and the button has a click target.
    message.info('Save is not yet wired up — edits are in memory only.');
  };

  // ── Handlers: selection / delete ─────────────────────────────────────
  const handleSelectKey = (key) => setSelectedKey(key);

  const handleDeleteKey = (key) => {
    setSectionTypes(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (selectedKey === key) {
      const remaining = allKeys.filter(k => k !== key);
      setSelectedKey(remaining.length > 0 ? remaining[0] : null);
    }
  };

  // ── Handlers: description / header_variants edit ─────────────────────
  const handleDescriptionChange = (e) => {
    const value = e.target.value;
    setSectionTypes(prev => ({
      ...prev,
      [selectedKey]: { ...prev[selectedKey], description: value },
    }));
  };

  const handleVariantChange = (index, value) => {
    setSectionTypes(prev => {
      const variants = [...(prev[selectedKey].header_variants || [])];
      variants[index] = value;
      return {
        ...prev,
        [selectedKey]: { ...prev[selectedKey], header_variants: variants },
      };
    });
  };

  const handleAddVariant = () => {
    setSectionTypes(prev => {
      const variants = [...(prev[selectedKey].header_variants || []), ''];
      return {
        ...prev,
        [selectedKey]: { ...prev[selectedKey], header_variants: variants },
      };
    });
  };

  const handleRemoveVariant = (index) => {
    setSectionTypes(prev => {
      const variants = (prev[selectedKey].header_variants || []).filter((_, i) => i !== index);
      return {
        ...prev,
        [selectedKey]: { ...prev[selectedKey], header_variants: variants },
      };
    });
  };

  const handleNoSupportChange = (e) => {
    const checked = e.target.checked;
    setSectionTypes(prev => ({
      ...prev,
      [selectedKey]: { ...prev[selectedKey], no_code_support_needed: checked },
    }));
  };

  // ── Handlers: Add-new modal ──────────────────────────────────────────
  const openAddModal = () => {
    setNewKey('');
    setNewDescription('');
    setNewVariantsText('');
    setNewKeyError(null);
    setNewNoCodeSupport(false);
    setAddModalOpen(true);
  };

  const handleAddSubmit = () => {
    const trimmedKey = newKey.trim();
    if (!trimmedKey) {
      setNewKeyError('Section type key is required.');
      return;
    }
    if (sectionTypes[trimmedKey]) {
      setNewKeyError(`A section type with key "${trimmedKey}" already exists.`);
      return;
    }

    // Parse variants: one per line, trim each, drop blanks
    const variants = newVariantsText
      .split('\n')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    setSectionTypes(prev => ({
      ...prev,
      [trimmedKey]: {
        description: newDescription.trim(),
        header_variants: variants,
        no_code_support_needed: newNoCodeSupport,
      },
    }));

    setSelectedKey(trimmedKey);
    setAddModalOpen(false);
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Sider (mirrors WorkQueuePage) ───────────────────────────── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 10,
        }}
      >
        <div style={{
          display: 'flex',
          background: 'linear-gradient(180deg, #162349 0%, #2a4a7c 100%)',
          flexDirection: 'column',
          justifyContent: 'space-between',
          height: '100%',
          alignItems: 'center',
          padding: '20px 0',
        }}>
          <div></div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <Tooltip title={username} placement="right">
              <Avatar
                style={{
                  backgroundColor: 'white',
                  color: '#162349',
                  cursor: 'pointer',
                  border: '2px solid white',
                }}
                icon={<UserOutlined />}
              />
            </Tooltip>

            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>

            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      <Layout style={{ marginLeft: 60 }}>

        {/* ── Header (WorkQueuePage pattern) ───────────────────────── */}
        <Header style={{
          background: '#162349',
          padding: '0 20px',
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          {/* Breadcrumb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <span
              onClick={() => navigate('/hcc-config')}
              style={{
                color: 'rgba(255,255,255,0.75)',
                fontSize: 20,
                fontWeight: 700,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
              }}
              onMouseEnter={e => (e.currentTarget.style.color = 'white')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            >
              HCC STUDIO
            </span>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: 20 }}>/</span>
            <span style={{
              color: 'white',
              fontSize: 20,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              Section Definitions
            </span>
          </div>

          {/* Lock + Save */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {canEdit ? (
              <>
                <Tooltip title={editMode ? 'Click to lock (return to view-only)' : 'Click to unlock for editing'}>
                  <Button
                    icon={editMode ? <UnlockOutlined /> : <LockOutlined />}
                    onClick={handleLockToggle}
                    style={{
                      backgroundColor: editMode ? '#FEF3C7' : 'white',
                      color: '#162349',
                      fontWeight: 600,
                      borderColor: editMode ? '#F59E0B' : '#d9d9d9',
                    }}
                  >
                    {editMode ? 'Unlocked' : 'Locked'}
                  </Button>
                </Tooltip>
                <Tooltip title={editMode ? 'Save (not yet wired up)' : 'Unlock to enable saving'}>
                  <Button
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    disabled={!editMode}
                    style={{ backgroundColor: 'white', color: '#162349', fontWeight: 600 }}
                  >
                    Save
                  </Button>
                </Tooltip>
              </>
            ) : (
              <Tag
                style={{
                  backgroundColor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.3)',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '4px 12px',
                }}
              >
                Display Only
              </Tag>
            )}
          </div>


        </Header>

        {/* ── Content ─────────────────────────────────────────────────── */}
        <Content style={{
          padding: '28px 32px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
          minHeight: 'calc(100vh - 64px)',
        }}>

          {/* Loading */}
          {loading && (
            <div style={{ textAlign: 'center', padding: '80px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16, color: '#6B7280', fontSize: 14 }}>
                Loading section definitions…
              </div>
            </div>
          )}

          {/* Load error */}
          {!loading && loadError && (
            <Alert
              type="warning"
              showIcon
              message="Configuration file not found"
              description={
                <span>
                  Could not load <code>{SECTION_DEFINITIONS_URL}</code>. ({loadError})
                </span>
              }
              style={{ maxWidth: 640, margin: '40px auto' }}
            />
          )}

          {/* Editor */}
          {!loading && !loadError && (
            <div style={{
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              overflow: 'hidden',
              display: 'flex',
              minHeight: 'calc(100vh - 64px - 56px)',
            }}>

              {/* ── Left panel: keys list ───────────────────────────── */}
              <div style={{
                width: 320,
                flexShrink: 0,
                borderRight: '1px solid #E5E7EB',
                display: 'flex',
                flexDirection: 'column',
                background: '#FAFAFA',
              }}>
                {/* Add-new */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid #E5E7EB' }}>
                  <Tooltip title={editMode ? '' : 'Unlock to add new section types'}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={openAddModal}
                      disabled={!editMode}
                      block
                      style={{
                        backgroundColor: editMode ? '#162349' : undefined,
                        color: editMode ? 'white' : undefined,
                        fontWeight: 600,
                      }}
                    >
                      Add new section type
                    </Button>
                  </Tooltip>
                </div>

                {/* Search */}
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>
                  <Input
                    prefix={<SearchOutlined style={{ color: '#9CA3AF' }} />}
                    placeholder="Search keys…"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    allowClear
                  />
                </div>

                {/* List */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                  {filteredKeys.length === 0 ? (
                    <div style={{ padding: '40px 16px', textAlign: 'center' }}>
                      <Text type="secondary" style={{ fontSize: 13 }}>
                        {searchText
                          ? `No keys match "${searchText}"`
                          : 'No section types defined.'}
                      </Text>
                    </div>
                  ) : (
                    filteredKeys.map(key => {
                      const isSelected = key === selectedKey;
                      return (
                        <div
                          key={key}
                          onClick={() => handleSelectKey(key)}
                          style={{
                            padding: '10px 16px',
                            cursor: 'pointer',
                            background: isSelected ? '#E0E7FF' : 'transparent',
                            borderLeft: isSelected ? '3px solid #162349' : '3px solid transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 8,
                            transition: 'background 0.12s',
                          }}
                          onMouseEnter={e => {
                            if (!isSelected) e.currentTarget.style.background = '#F0F4FF';
                          }}
                          onMouseLeave={e => {
                            if (!isSelected) e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          <span style={{
                            fontFamily: 'monospace',
                            fontSize: 13,
                            color: '#111827',
                            fontWeight: isSelected ? 600 : 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {key}
                          </span>

                          {editMode && (
                            <Popconfirm
                              title="Delete this section type?"
                              description={`Remove "${key}" and all its header variants?`}
                              onConfirm={(e) => {
                                if (e && e.stopPropagation) e.stopPropagation();
                                handleDeleteKey(key);
                              }}
                              onCancel={(e) => {
                                if (e && e.stopPropagation) e.stopPropagation();
                              }}
                              okText="Delete"
                              cancelText="Cancel"
                              okButtonProps={{ danger: true }}
                            >
                              <Button
                                size="small"
                                type="text"
                                icon={<DeleteOutlined style={{ color: '#DC2626' }} />}
                                onClick={e => e.stopPropagation()}
                              />
                            </Popconfirm>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Footer count */}
                <div style={{
                  padding: '10px 16px',
                  borderTop: '1px solid #E5E7EB',
                  background: 'white',
                }}>
                  <Text style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {filteredKeys.length} of {allKeys.length} section types
                  </Text>
                </div>
              </div>

              {/* ── Right panel: description + variants ─────────────── */}
              <div style={{ flex: 1, padding: '24px 32px', overflowY: 'auto' }}>
                {!selectedKey || !selectedEntry ? (
                  <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <Empty description="Select a section type to view its details." />
                  </div>
                ) : (
                  <>
                    {/* Key heading */}
                    <div style={{ marginBottom: 24 }}>
                      <Text style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#9CA3AF',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                      }}>
                        Section type key
                      </Text>
                      <Title level={3} style={{
                        margin: '4px 0 0 0',
                        fontFamily: 'monospace',
                        color: '#111827',
                      }}>
                        {selectedKey}
                      </Title>
                    </div>

                    {/* Description */}
                    <div style={{ marginBottom: 32 }}>
                      <Text style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: '#9CA3AF',
                        letterSpacing: '0.08em',
                        textTransform: 'uppercase',
                        display: 'block',
                        marginBottom: 8,
                      }}>
                        Description
                      </Text>
                      {editMode ? (
                        <TextArea
                          value={selectedEntry.description || ''}
                          onChange={handleDescriptionChange}
                          rows={5}
                          placeholder="Describe what this section type captures…"
                        />
                      ) : (
                        <Paragraph style={{
                          color: '#374151',
                          fontSize: 14,
                          marginBottom: 0,
                          whiteSpace: 'pre-wrap',
                        }}>
                          {selectedEntry.description || (
                            <span style={{ color: '#D1D5DB' }}>(No description.)</span>
                          )}
                        </Paragraph>
                      )}
                    </div>

                    {/* Header variants */}
                    <div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 8,
                      }}>
                        <Text style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#9CA3AF',
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                        }}>
                          Header variants ({(selectedEntry.header_variants || []).length})
                        </Text>
                        {editMode && (
                          <Button
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={handleAddVariant}
                          >
                            Add variant
                          </Button>
                        )}
                      </div>

                      {(selectedEntry.header_variants || []).length === 0 ? (
                        <Text type="secondary" style={{ fontSize: 13, fontStyle: 'italic' }}>
                          No header variants defined.
                        </Text>
                      ) : editMode ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {(selectedEntry.header_variants || []).map((variant, idx) => (
                            <div key={idx} style={{ display: 'flex', gap: 8 }}>
                              <Input
                                value={variant}
                                onChange={e => handleVariantChange(idx, e.target.value)}
                                placeholder="Header variant text…"
                              />
                              <Button
                                icon={<CloseOutlined />}
                                onClick={() => handleRemoveVariant(idx)}
                                danger
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {(selectedEntry.header_variants || []).map((variant, idx) => (
                            <span
                              key={idx}
                              style={{
                                display: 'inline-block',
                                padding: '4px 10px',
                                borderRadius: 4,
                                fontSize: 13,
                                background: '#F3F4F6',
                                color: '#374151',
                                border: '1px solid #E5E7EB',
                              }}
                            >
                              {variant}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* No-code-support checkbox */}
                    <div style={{
                      marginTop: 32,
                      paddingTop: 20,
                      borderTop: '1px solid #E5E7EB',
                    }}>
                      <Checkbox
                        checked={!!selectedEntry.no_code_support_needed}
                        onChange={handleNoSupportChange}
                        disabled={!editMode}
                      >
                        <span style={{
                          fontSize: 14,
                          color: editMode ? '#111827' : '#374151',
                        }}>
                          Codes appearing in this section does NOT need support.
                        </span>
                      </Checkbox>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

        </Content>
      </Layout>

      {/* ── Add-new modal ─────────────────────────────────────────────── */}
      <Modal
        title="Add new section type"
        open={addModalOpen}
        onCancel={() => setAddModalOpen(false)}
        onOk={handleAddSubmit}
        okText="Add"
        cancelText="Cancel"
        destroyOnClose
        width={560}
      >
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="Section type key"
            required
            validateStatus={newKeyError ? 'error' : undefined}
            help={newKeyError || 'Use snake_case, e.g. "imaging_followup".'}
          >
            <Input
              value={newKey}
              onChange={e => {
                setNewKey(e.target.value);
                if (newKeyError) setNewKeyError(null);
              }}
              placeholder="e.g., my_new_section"
              autoFocus
            />
          </Form.Item>

          <Form.Item label="Description">
            <TextArea
              value={newDescription}
              onChange={e => setNewDescription(e.target.value)}
              rows={3}
              placeholder="Describe what this section type captures…"
            />
          </Form.Item>

          <Form.Item
            label="Header variants"
            help="One variant per line. Leave blank if no variants yet."
          >
            <TextArea
              value={newVariantsText}
              onChange={e => setNewVariantsText(e.target.value)}
              rows={6}
              placeholder={`Variant 1\nVariant 2\nVariant 3`}
              style={{ fontFamily: 'monospace', fontSize: 13 }}
            />
          </Form.Item>

          <Form.Item>
            <Checkbox
              checked={newNoCodeSupport}
              onChange={e => setNewNoCodeSupport(e.target.checked)}
            >
              Codes appearing in this section does NOT need support.
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>

    </Layout>
  );
};

export default SectionDefinitionsEditor;